<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  targetSpec: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const courierAppLabels = computed(() => ({
  boltfood: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.BOLTFOOD'),
  ubereats: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.UBEREATS'),
  glovo: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.GLOVO'),
  wolt: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.WOLT'),
  stuart: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.APPS.STUART'),
}));

const languageLabels = computed(() => ({
  en: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES.EN'),
  ru: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES.RU'),
  pl: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES.PL'),
  uk: t('BROADCASTS.COMPOSER.GLOBAL_FILTERS.LANGUAGES.UK'),
}));

const conversationFilters = computed(() =>
  Object.entries(props.targetSpec.conversation_filters || {}).filter(
    ([, values]) => values.length
  )
);

const operationLabel = operation =>
  operation === 'include'
    ? t('BROADCASTS.COMPOSER.SOURCE.INCLUDE')
    : t('BROADCASTS.COMPOSER.SOURCE.EXCLUDE');

const sourceTypeLabel = type =>
  type === 'team'
    ? t('BROADCASTS.COMPOSER.SOURCE.TEAM_TITLE')
    : t('BROADCASTS.COMPOSER.SOURCE.IDENTIFIERS_TITLE');

const identifierTypeLabel = type => {
  if (type === 'telegram_id') {
    return t('BROADCASTS.COMPOSER.SOURCE.TELEGRAM_ID');
  }
  if (type === 'email') return t('BROADCASTS.COMPOSER.SOURCE.EMAIL');
  return t('BROADCASTS.COMPOSER.SOURCE.PHONE');
};

const filterLabel = filter => {
  if (filter === 'statuses') {
    return t('BROADCASTS.TARGET_SPEC.STATUSES');
  }
  if (filter === 'labels_all') {
    return t('BROADCASTS.TARGET_SPEC.LABELS_ALL');
  }
  if (filter === 'labels_any') {
    return t('BROADCASTS.TARGET_SPEC.LABELS_ANY');
  }
  if (filter === 'courier_apps') {
    return t('BROADCASTS.TARGET_SPEC.COURIER_APPS');
  }
  if (filter === 'languages') {
    return t('BROADCASTS.TARGET_SPEC.LANGUAGES');
  }
  return filter;
};

const filterValues = (filter, values) => {
  if (filter === 'courier_apps') {
    return values.map(value => courierAppLabels.value[value] || value);
  }
  if (filter === 'languages') {
    return values.map(value => languageLabels.value[value] || value);
  }
  return values;
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap gap-2 text-xs text-n-slate-11">
      <span class="rounded-md bg-n-alpha-2 px-2 py-1">
        {{
          t('BROADCASTS.TARGET_SPEC.INBOX', {
            inboxId: targetSpec.inbox_id,
          })
        }}
      </span>
      <span class="rounded-md bg-n-alpha-2 px-2 py-1">
        {{
          t('BROADCASTS.TARGET_SPEC.VERSION', { version: targetSpec.version })
        }}
      </span>
    </div>

    <p class="text-xs text-n-slate-10">
      {{ t('BROADCASTS.COMPOSER.SET_SEMANTICS') }}
    </p>

    <div v-if="conversationFilters.length" class="flex flex-wrap gap-2">
      <span
        v-for="[filter, values] in conversationFilters"
        :key="filter"
        class="rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-11"
      >
        {{ filterLabel(filter) }}: {{ filterValues(filter, values).join(', ') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <div
        v-for="(source, sourceIndex) in targetSpec.sources"
        :key="source.key"
        class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
      >
        <div class="flex flex-wrap items-center gap-2 text-xs">
          <span class="font-medium text-n-slate-12">
            {{
              t('BROADCASTS.TARGET_SPEC.SOURCE', {
                number: sourceIndex + 1,
              })
            }}
          </span>
          <span
            class="rounded-md bg-n-alpha-2 px-2 py-1 font-medium text-n-slate-11"
          >
            {{ operationLabel(source.operation) }} ·
            {{ sourceTypeLabel(source.type) }}
          </span>
        </div>

        <div
          v-if="source.team_ids?.length"
          class="mt-2 text-xs text-n-slate-11"
        >
          {{
            t('BROADCASTS.TARGET_SPEC.TEAMS', {
              teamIds: source.team_ids.join(', '),
            })
          }}
        </div>

        <template v-if="source.type === 'identifiers'">
          <div class="mt-2 text-xs text-n-slate-11">
            {{
              t('BROADCASTS.TARGET_SPEC.IDENTIFIER_COUNT', {
                type: identifierTypeLabel(source.identifier_type),
                count: source.values.length,
              })
            }}
          </div>
          <div class="mt-2 flex flex-wrap gap-1">
            <span
              v-for="(value, valueIndex) in source.values.slice(0, 10)"
              :key="`${source.key}-${valueIndex}`"
              class="max-w-full truncate rounded bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
            >
              {{ value }}
            </span>
            <span
              v-if="source.values.length > 10"
              class="rounded bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-10"
            >
              {{
                t('BROADCASTS.TARGET_SPEC.MORE_VALUES', {
                  count: source.values.length - 10,
                })
              }}
            </span>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
